import {
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useEffect
} from 'react';
import {
  ScheduleComponent,
  ViewsDirective,
  ViewDirective,
  TimelineViews,
  ResourcesDirective,
  ResourceDirective,
  Inject,
  type EventSettingsModel,
  type ResourceDetails
} from '@syncfusion/ej2-react-schedule';
import { useTranslation } from 'react-i18next';
import type { GanttVisualizationProfileDto } from '../api';
import type {
  ProductionScheduleRendererProps,
  ProductionScheduleViewHandle,
  ZoomLevel
} from './ProductionScheduleViewTypes';
import { ZOOM_LEVEL_INTERVAL_MINUTES } from './ProductionScheduleViewTypes';

export type { ProductionScheduleViewHandle };

interface ScheduleEventItem {
  Id: string | number;
  Subject: string;
  StartTime: Date;
  EndTime: Date;
  ResourceId: string | number;
  CategoryColor?: string;
  ProductName?: string;
  ItemType?: 'operation' | 'setup';
  Resources?: Array<string | number>;
}

const zoomConfig: Record<ZoomLevel, { interval: number; slotCount: number }> = {
  '5min': { interval: ZOOM_LEVEL_INTERVAL_MINUTES['5min'], slotCount: 1 },
  '10min': { interval: ZOOM_LEVEL_INTERVAL_MINUTES['10min'], slotCount: 1 },
  '15min': { interval: ZOOM_LEVEL_INTERVAL_MINUTES['15min'], slotCount: 1 },
  '30min': { interval: ZOOM_LEVEL_INTERVAL_MINUTES['30min'], slotCount: 1 },
  '60min': { interval: ZOOM_LEVEL_INTERVAL_MINUTES['60min'], slotCount: 1 },
  '120min': { interval: ZOOM_LEVEL_INTERVAL_MINUTES['120min'], slotCount: 2 }
};

const roundDownToInterval = (date: Date, intervalMinutes: number): Date => {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  rounded.setMinutes(Math.floor(minutes / intervalMinutes) * intervalMinutes, 0, 0);
  return rounded;
};

const roundUpToInterval = (date: Date, intervalMinutes: number): Date => {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  const remainder = minutes % intervalMinutes;
  if (remainder !== 0) {
    rounded.setMinutes(minutes + (intervalMinutes - remainder), 0, 0);
  } else {
    rounded.setSeconds(0, 0);
  }
  return rounded;
};

const toTimeString = (date: Date): string =>
  `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;

const ProductionScheduleScheduleRenderer = forwardRef<
  ProductionScheduleViewHandle,
  ProductionScheduleRendererProps
>(
  (
    {
      timeWindow,
      now,
      operations,
      setups = [],
      resources,
      zoomLevel = '10min',
      visualProfile,
      onVisualProfileChange,
      onZoomToFit,
      height = '100%',
      width = '100%',
      readonly = true
    },
    ref
  ) => {
    const { t, i18n } = useTranslation(['common']);
    const scheduleRef = useRef<any>(null);
    const isInitialMountRef = useRef(true);

    const currentZoom = zoomConfig[zoomLevel] ?? zoomConfig['10min'];

    // Always use plan start so that past plans are shown from their start date, not "today"
    const scheduleStartDate = useMemo(() => {
      const day = new Date(timeWindow.start);
      day.setHours(0, 0, 0, 0);
      return day;
    }, [timeWindow.start]);

    const { scheduleStartHour, scheduleEndHour } = useMemo(() => {
      const start = roundDownToInterval(timeWindow.start, currentZoom.interval);
      const end = roundUpToInterval(timeWindow.end, currentZoom.interval);
      const startStr = toTimeString(start);
      const endStr = toTimeString(end);
      // If window spans midnight, Syncfusion needs full-day range
      const endBeforeStart = timeWindow.end.getDate() !== timeWindow.start.getDate() || end.getTime() <= start.getTime();
      return {
        scheduleStartHour: endBeforeStart ? '00:00' : startStr,
        scheduleEndHour: endBeforeStart ? '23:59' : endStr
      };
    }, [timeWindow.start, timeWindow.end, currentZoom.interval]);

    const firstOperationTime = useMemo(() => {
      const allStarts = [
        ...operations.map(o => o.startTime.getTime()),
        ...setups.map(s => s.startTime.getTime())
      ];
      if (allStarts.length === 0) return scheduleStartHour;
      const earliest = new Date(Math.min(...allStarts));
      return toTimeString(earliest);
    }, [operations, setups, scheduleStartHour]);

    useImperativeHandle(
      ref,
      () => ({
        scrollToStart: () => {
          const instance = scheduleRef.current?.scheduleObj ?? scheduleRef.current;
          instance?.scrollTo?.(firstOperationTime);
        },
        scrollToEnd: () => {
          const instance = scheduleRef.current?.scheduleObj ?? scheduleRef.current;
          if (!instance) return;
          instance.scrollTo?.(scheduleEndHour);
          setTimeout(() => {
            const scheduleElement = scheduleRef.current?.element ?? scheduleRef.current?.getRootElement?.();
            if (!scheduleElement) return;
            const contentArea =
              scheduleElement.querySelector?.('.e-content-wrap') ||
              scheduleElement.querySelector?.('.e-timeline-view') ||
              scheduleElement.querySelector?.('.e-content-table');
            if (contentArea) {
              contentArea.scrollLeft = contentArea.scrollWidth - contentArea.clientWidth;
            }
          }, 100);
        },
        scrollToNow: () => {
          if (!now) return;
          const instance = scheduleRef.current?.scheduleObj ?? scheduleRef.current;
          instance?.scrollTo?.(toTimeString(now));
        },
        zoomToFit: () => {
          onZoomToFit?.();
        },
        setVisualProfile: (profile: GanttVisualizationProfileDto) => {
          onVisualProfileChange?.(profile);
        }
      }),
      [now, scheduleEndHour, scheduleStartHour, firstOperationTime, onZoomToFit, onVisualProfileChange]
    );

    const resourceIds = useMemo(() => new Set(resources.map(r => String(r.id))), [resources]);

    const events = useMemo<ScheduleEventItem[]>(() => {
      const opFiltered = operations.filter(op => op.resourceId != null && resourceIds.has(String(op.resourceId)));
      const setupFiltered = setups.filter(s => s.resourceId != null && resourceIds.has(String(s.resourceId)));
      if (operations.length > 0 && opFiltered.length === 0) {
        const sampleOp = operations[0];
        const sampleIds = Array.from(resourceIds).slice(0, 3);
        console.error('[ProductionSchedule] Alle Operationen gefiltert: resourceId-Mismatch', {
          operationsCount: operations.length,
          resourcesCount: resources.length,
          sampleOpResourceId: sampleOp?.resourceId,
          sampleResourceIds: sampleIds
        });
      }
      const operationEvents: ScheduleEventItem[] = opFiltered
        .map(operation => ({
          Id: operation.id,
          Subject: operation.subject,
          StartTime: operation.startTime,
          EndTime: operation.endTime,
          ResourceId: operation.resourceId,
          CategoryColor: operation.color,
          ProductName: operation.productName,
          ItemType: 'operation' as const,
          Resources: [operation.resourceId]
        }));

      const setupEvents: ScheduleEventItem[] = setupFiltered
        .map(setup => ({
          Id: `setup-${setup.id}`,
          Subject: setup.subject,
          StartTime: setup.startTime,
          EndTime: setup.endTime,
          ResourceId: setup.resourceId,
          CategoryColor: setup.color ?? '#000000',
          ProductName: setup.productName,
          ItemType: 'setup' as const,
          Resources: [setup.resourceId]
        }));

      return [...operationEvents, ...setupEvents];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resources.length excluded to prevent Syncfusion re-render race condition
    }, [operations, setups, resourceIds]);

    const resourceData = useMemo(
      () =>
        resources.map(resource => ({
          text: resource.name,
          id: resource.id,
          color: resource.color,
          type: resource.type || ''
        })),
      [resources]
    );

    const eventSettings: EventSettingsModel = useMemo(
      () => ({
        dataSource: events,
        fields: {
          id: 'Id',
          subject: { name: 'Subject' },
          startTime: { name: 'StartTime' },
          endTime: { name: 'EndTime' },
          resource: { name: 'Resources' }
        }
      }),
      [events]
    );

    const resourceHeaderTemplate = (props: ResourceDetails) => {
      const data = props.resourceData;
      return (
        <div className="template-wrap">
          <div className="resource-name" style={{ fontWeight: 'bold', fontSize: '14px' }}>
            {data.text}
          </div>
          {data.type && (
            <div className="resource-type" style={{ fontSize: '12px', color: '#666' }}>
              {data.type}
            </div>
          )}
        </div>
      );
    };

    const dateHeaderTemplate = (props: any) => {
      const date = props.date as Date;
      if (!date) return null;
      const dayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
      const dayName = dayNames[date.getDay()];
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return (
        <span>
          {dayName}, {day}.{month}.
        </span>
      );
    };

    const formatDateTime = useCallback(
      (value?: Date) => {
        if (!value) return '';
        return value.toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
      [i18n.language]
    );

    const quickInfoTemplates = useMemo(
      () => ({
        content: (props: any) => {
          const event = props as ScheduleEventItem;
          const productName = event.ProductName || t('common.notSet', 'Nicht gesetzt');
          return (
            <div className="e-popup-content">
              <div className="e-subject">{event.Subject}</div>
              <div className="mt-2 text-sm">
                <div>
                  <strong>{t('common.product', 'Produkt')}:</strong> {productName}
                </div>
                <div>
                  <strong>{t('common.start', 'Start')}:</strong> {formatDateTime(event.StartTime)}
                </div>
                <div>
                  <strong>{t('common.end', 'Ende')}:</strong> {formatDateTime(event.EndTime)}
                </div>
              </div>
            </div>
          );
        }
      }),
      [formatDateTime, t]
    );

    const eventRendered = useCallback(
      (args: any) => {
        const event = args.data;
        if (event.CategoryColor) {
          args.element.style.backgroundColor = event.CategoryColor;
          args.element.style.borderColor = event.CategoryColor;
        }

        const displayConfig = visualProfile?.displayConfiguration as any;
        if (displayConfig) {
          const taskLabel = displayConfig.taskLabel;
          if (taskLabel?.enabled === false) {
            const labelElement = args.element.querySelector('.e-subject');
            if (labelElement) {
              labelElement.style.display = 'none';
            }
          } else if (taskLabel?.template) {
            const labelElement = args.element.querySelector('.e-subject');
            if (labelElement) {
              const labelText = taskLabel.template
                .replace('{operationName}', event.Subject?.split(' - ')[1] || '')
                .replace('{orderNumber}', event.Subject?.split(' - ')[0] || '');
              labelElement.textContent = labelText;
            }
          }

          const statusIndicator = displayConfig.statusIndicator;
          if (statusIndicator?.enabled === false) {
            const statusElement = args.element.querySelector('.e-status-indicator');
            if (statusElement) {
              statusElement.style.display = 'none';
            }
          }
        }
      },
      [visualProfile]
    );

    const timeScale = useMemo(
      () => ({
        enable: true,
        interval: currentZoom.interval,
        slotCount: currentZoom.slotCount
      }),
      [currentZoom.interval, currentZoom.slotCount]
    );

    const handleScheduleCreated = useCallback(() => {
      isInitialMountRef.current = false;
      const instance = scheduleRef.current?.scheduleObj ?? scheduleRef.current;
      if (!instance) return;
      instance.timeScale = {
        enable: true,
        interval: currentZoom.interval,
        slotCount: currentZoom.slotCount
      };
      instance.dataBind?.();
    }, [currentZoom.interval, currentZoom.slotCount]);

    useEffect(() => {
      if (isInitialMountRef.current) return;
      const instance = scheduleRef.current?.scheduleObj ?? scheduleRef.current;
      if (!instance?.timeScale) return;
      instance.timeScale.interval = currentZoom.interval;
      instance.timeScale.slotCount = currentZoom.slotCount;
      try {
        instance.changeView?.(instance.currentView, instance.selectedDate);
      } catch {
        // Fallback: Syncfusion updates on next render
      }
    }, [currentZoom.interval, currentZoom.slotCount]);

    return (
      <ScheduleComponent
        ref={scheduleRef}
        width={width}
        height={height}
        eventSettings={eventSettings}
        readonly={readonly}
        showQuickInfo={true}
        quickInfoTemplates={quickInfoTemplates}
        allowDragAndDrop={false}
        allowResizing={false}
        showWeekend={true}
        currentView="TimelineDay"
        selectedDate={scheduleStartDate}
        rowAutoHeight={false}
        showTimeIndicator={true}
        startHour={scheduleStartHour}
        endHour={scheduleEndHour}
        workHours={{ highlight: true, start: scheduleStartHour, end: scheduleEndHour }}
        showHeaderBar={false}
        group={{ enableCompactView: false, resources: ['Resources'] }}
        resourceHeaderTemplate={resourceHeaderTemplate}
        dateHeaderTemplate={dateHeaderTemplate}
        timeScale={timeScale}
        dateFormat="EEEE, dd.MM."
        timeFormat="HH:mm"
        eventRendered={eventRendered}
        created={handleScheduleCreated}
        locale={i18n.language === 'de' ? 'de' : 'en'}
      >
        <ViewsDirective>
          <ViewDirective option="TimelineDay" interval={2} />
        </ViewsDirective>
        <ResourcesDirective>
          <ResourceDirective
            dataSource={resourceData}
            field="Resources"
            title={t('shiftPlan.resources')}
            name="Resources"
            textField="text"
            idField="id"
            colorField="color"
            allowMultiple={false}
          />
        </ResourcesDirective>
        <Inject services={[TimelineViews]} />
      </ScheduleComponent>
    );
  }
);

ProductionScheduleScheduleRenderer.displayName = 'ProductionScheduleScheduleRenderer';

export default ProductionScheduleScheduleRenderer;
